import type { ICategoryComparison } from "app/pages/industry-analysis/categories-context";

import type { IKeyIndicatorsData } from "../hooks/useKeyIndicatorsData";

import type { KeySizingComparisonRow } from "./types";

/**
 * Calculates the maximum total visits value for the current period
 */
const calculateMaxCurrentTotalVisits = (data: IKeyIndicatorsData[], isPeriodOverPeriod: boolean): number => {
    const values = data.map((item) => {
        if (isPeriodOverPeriod && item.rawDataPop) {
            // In PoP mode, TotalVisits is an array - get current period (index 0)
            return item.rawDataPop.TotalVisits[0]?.Value ?? 0;
        }
        // In regular mode, TotalVisits is a single object
        return item.rawData.TotalVisits?.Value ?? 0;
    });

    return Math.max(0, ...values);
};

/**
 * Calculates the maximum total visits value for the previous period (PoP mode only)
 */
const calculateMaxPreviousTotalVisits = (data: IKeyIndicatorsData[]): number => {
    const values = data.map((item) => {
        return item.rawDataPop?.TotalVisits?.[1]?.Value ?? 0;
    });

    return Math.max(0, ...values);
};

/**
 * Finds the data item for a specific category by matching the category key
 * in the AudienceOverview.Data structure (used in non-PoP mode only)
 */
const findCategoryDataItem = (
    data: IKeyIndicatorsData[],
    category: ICategoryComparison["category"],
): IKeyIndicatorsData | undefined => {
    const key = category.forApi;
    return data.find((item) => {
        const dataObj = item.rawData?.AudienceOverview?.Data;
        return !!dataObj && Object.prototype.hasOwnProperty.call(dataObj, key);
    });
};

/**
 * Creates total visits data structure with value and share
 */
const createTotalVisitsData = (value: number, maxValue: number): { value: number; share: number } => {
    return {
        value,
        share: maxValue > 0 ? value / maxValue : 0,
    };
};

/**
 * Extracts total visits data for period-over-period mode
 */
type TotalVisitsShape = { value: number; share: number };

const extractPoPTotalVisitsData = (
    dataItem: IKeyIndicatorsData,
    maxCurrentValue: number,
    maxPreviousValue: number,
): { currentTotalVisits: TotalVisitsShape; previousTotalVisits?: TotalVisitsShape; periodChange: number } => {
    const currentPeriodData = dataItem.rawDataPop?.TotalVisits?.[0];
    const previousPeriodData = dataItem.rawDataPop?.TotalVisits?.[1];

    const currentTotalVisits = createTotalVisitsData(currentPeriodData?.Value ?? 0, maxCurrentValue);

    const previousTotalVisits = previousPeriodData
        ? createTotalVisitsData(previousPeriodData.Value ?? 0, maxPreviousValue)
        : undefined;

    const periodChange = currentPeriodData?.Change ?? 0;

    return { currentTotalVisits, previousTotalVisits, periodChange };
};

/**
 * Extracts total visits data for regular mode
 */
const extractRegularTotalVisitsData = (
    dataItem: IKeyIndicatorsData,
    maxCurrentValue: number,
): { currentTotalVisits: TotalVisitsShape; previousTotalVisits?: undefined; periodChange: number } => {
    const currentTotalVisits = createTotalVisitsData(dataItem.rawData.TotalVisits?.Value ?? 0, maxCurrentValue);

    const periodChange = dataItem.rawData.TotalVisits?.Change ?? 0;

    return { currentTotalVisits, previousTotalVisits: undefined, periodChange };
};

/**
 * Extracts common metrics (visits per visitor, unbounced visits, pages per visit, visit duration)
 * from the Average data. In PoP mode, extracts both current and previous period values.
 */
const extractCommonMetrics = (
    dataItem: IKeyIndicatorsData,
    isPeriodOverPeriod: boolean,
): {
    visitsPerVisitor: number;
    unbouncedVisits: number;
    pagesPerVisit: number;
    visitDuration: number;
    previousVisitsPerVisitor?: number;
    previousUnbouncedVisits?: number;
    previousPagesPerVisit?: number;
    previousVisitDuration?: number;
} => {
    if (isPeriodOverPeriod && dataItem.rawDataPop?.Average) {
        const avg = dataItem.rawDataPop.Average;

        return {
            visitsPerVisitor: avg.VisitsPerVisitor?.Value ?? 0,
            unbouncedVisits: avg.TotalUnbouncedVisits?.Value ?? 0,
            pagesPerVisit: avg.PagesPerVisit?.Value ?? 0,
            visitDuration: avg.VisitDuration?.Value ?? 0,
            previousVisitsPerVisitor: avg.VisitsPerVisitor?.PreviousPeriodValue,
            previousUnbouncedVisits: avg.TotalUnbouncedVisits?.PreviousPeriodValue,
            previousPagesPerVisit: avg.PagesPerVisit?.PreviousPeriodValue,
            previousVisitDuration: avg.VisitDuration?.PreviousPeriodValue,
        };
    }

    const avg = dataItem.rawData?.Average;

    return {
        visitsPerVisitor: avg?.VisitsPerVisitor ?? 0,
        unbouncedVisits: avg?.TotalUnbouncedVisits ?? 0,
        pagesPerVisit: avg?.PagesPerVisit ?? 0,
        visitDuration: avg?.VisitDuration ?? 0,
    };
};

/**
 * Transforms a single category and its data into a table row
 */
const transformCategoryToRow = (
    category: ICategoryComparison,
    dataItem: IKeyIndicatorsData,
    isPeriodOverPeriod: boolean,
    maxCurrentValue: number,
    maxPreviousValue: number,
): KeySizingComparisonRow => {
    const visitsData =
        isPeriodOverPeriod && dataItem.rawDataPop
            ? extractPoPTotalVisitsData(dataItem, maxCurrentValue, maxPreviousValue)
            : extractRegularTotalVisitsData(dataItem, maxCurrentValue);

    const commonMetrics = extractCommonMetrics(dataItem, isPeriodOverPeriod);

    const result: KeySizingComparisonRow = {
        color: category.color,
        categoryName: category.category.text || category.category.name,
        totalVisits: visitsData.currentTotalVisits,
        periodChange: visitsData.periodChange,
        ...commonMetrics,
    };

    if (visitsData.previousTotalVisits) {
        result.previousTotalVisits = visitsData.previousTotalVisits;
    }

    return result;
};

export const buildKeySizingComparisonTableData = (
    categories: ICategoryComparison[],
    data: IKeyIndicatorsData[],
    isPeriodOverPeriod: boolean,
): KeySizingComparisonRow[] => {
    if (!data || !categories || categories.length === 0) {
        return [];
    }

    const maxCurrentTotalVisitsValue = calculateMaxCurrentTotalVisits(data, isPeriodOverPeriod);
    const maxPreviousTotalVisitsValue = isPeriodOverPeriod ? calculateMaxPreviousTotalVisits(data) : 0;

    return categories
        .map((categoryComparison, index) => {
            /**
             * In PoP mode: Use index-based mapping since rawDataPop lacks AudienceOverview.Data
             * and data is fetched per category (arrays are aligned by index).
             * In regular mode: Use findCategoryDataItem to match by AudienceOverview.Data key.
             */
            const dataItem = isPeriodOverPeriod ? data[index] : findCategoryDataItem(data, categoryComparison.category);

            if (!dataItem) {
                return null;
            }

            return transformCategoryToRow(
                categoryComparison,
                dataItem,
                isPeriodOverPeriod,
                maxCurrentTotalVisitsValue,
                maxPreviousTotalVisitsValue,
            );
        })
        .filter((row): row is KeySizingComparisonRow => row !== null);
};
